import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Container as MuiContainer } from '@material-ui/core';
import { AppBackground, palette } from '@dev/ee-components';
import { push as Push, replace as Replace } from 'redux-first-history';
import { AccountOptions, ProductCode } from '@dev/ee-api/ee-api-models';

import { getConfigStore } from '@api/store';
import { getAccountStore, LoginWithDisposableToken } from '@api/store/account/actions';
import { ChangeMenuSize } from '@api/store/config/actions';

import Auth from '../Authentication';
import Loader from '../PageLoader';
import { useMenuItems } from './hooks/useMenuItems';
import { LayoutProps } from './LayoutInterface';
import { BillingPaths, HomePaths, pathsParams, UpsellingPaths } from '../dictionaries/paths';
import { getSessionAuth, SessionFailedAuth } from '@api/store/session/actions';
import { Menu } from '@dev/ee-modules';
import { isAccountActivated, isReseller } from '../Security';
import { useMobile } from '../hooks/useMobile';
import { useLocation, useRouteMatch } from 'react-router';
import { useAvailableSwitchProduct } from '../hooks/useAvailableSwitchProduct';
import { GetBillingPlans, getBillingPlanList } from '@api/store/products/actions';
import { isEmpty, includes } from 'lodash-es';
import { TemplatesPaths } from '@api/dashboards/Marketing/paths';
import { NofificationsBanners } from './NotificationsBanners';

const Container = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    margin: 0;
    padding: 0;
`;

const DesktopContent = styled.div`
    min-height: 100vh;
    flex-grow: 1;
`;

const Layout: FC<LayoutProps> = ({ children }: LayoutProps): JSX.Element => {
    const [isAccAuthenticated, setIsAccAuthenticated] = useState(Auth.isAuthenticated());
    const omitFirst = useRef(false);
    const config = useSelector(getConfigStore);
    const dispatch = useDispatch();
    const sessionIsAuth: boolean = useSelector(getSessionAuth);
    const billingPlans = useSelector(getBillingPlanList);
    const account = useSelector(getAccountStore);
    const isMobile = useMobile();
    const locationData = useLocation();
    const menuItems = useMenuItems();
    const isGutenbergEditor = useRouteMatch(TemplatesPaths?.TemplateEditorLinkName?.replace(pathsParams?.template, ''));
    const isAccountActive = isAccountActivated();

    const logout = (): void => {
        Auth.removeAccessToken();
        if (config?.bootedAsChild) {
            window.location.replace(location.origin);
            return;
        }

        dispatch(SessionFailedAuth());
        setIsAccAuthenticated(false);

        dispatch(Push('/'));
    };
    const { canSeeChangeProduct } = useAvailableSwitchProduct() || {};
    useEffect(() => {
        if (omitFirst?.current && sessionIsAuth !== isAccAuthenticated) {
            setIsAccAuthenticated(sessionIsAuth);
        }
        if (!!sessionIsAuth && isEmpty(billingPlans) && !isEmpty(account.GeneralInfo)) {
            dispatch(GetBillingPlans(ProductCode.EmailApi));
        }
        omitFirst.current = true;
    }, [sessionIsAuth, account.accountLoading]);

    const renderMenu = (): JSX.Element => {
        return (
            <>
                <Menu
                    config={config}
                    productCode={ProductCode.EmailApi}
                    Push={(url) => dispatch(Push(url))}
                    Replace={(url: string) => dispatch(Replace(url))}
                    menuItems={menuItems}
                    account={account}
                    location={{ ...locationData, origin: location.origin }}
                    isMobile={isMobile}
                    disableProductSwitcher
                    paths={{ ...HomePaths, ...BillingPaths, ...UpsellingPaths }}
                    logout={logout}
                    LoginWithDisposableToken={(params) => dispatch(LoginWithDisposableToken(params))}
                    isReseller={isReseller(account)}
                    ChangeMenuSize={(isOpen) => dispatch(ChangeMenuSize(isOpen))}
                    showChangeProduct={canSeeChangeProduct}
                />
            </>
        );
    };

    const requiredOnboardingSurvey = includes(account?.GeneralInfo?.Options, AccountOptions.RequiredOnboardingSurvey);
    const child = account?.accountLoading ? <Loader /> : children;

    if (
        requiredOnboardingSurvey ||
        !isAccAuthenticated ||
        !isAccountActive ||
        (isGutenbergEditor && TemplatesPaths?.TemplateEditorLinkName)
    ) {
        return <Fragment>{child}</Fragment>;
    }

    if (isMobile) {
        return (
            <>
                {renderMenu()}
                <MuiContainer
                    style={{
                        paddingTop: '55px',
                        paddingLeft: '0px',
                        paddingRight: '0px',
                        minHeight: '100vh',
                        background: palette.basic.background,
                    }}
                >
                    <NofificationsBanners />
                    {child}
                </MuiContainer>
            </>
        );
    }

    return (
        <>
            <AppBackground />
            <Container>
                {renderMenu()}
                <DesktopContent>
                    {' '}
                    <NofificationsBanners />
                    {child}
                </DesktopContent>
            </Container>
        </>
    );
};

export default Layout;
